<template>
  <!-- LOGGED-IN NAVBAR -->
  <nav
    v-if="session.isLoggedIn"
    class="app-navbar"
  >
    <div class="nav-container">
      <router-link class="brand" to="/">
        <img v-no-right-click src="/assets/logo.png" alt="Invi8" />
      </router-link>
      <div class="nav-links d-none d-lg-flex">
        <router-link class="nav-link" to="/host">Host</router-link>
        <router-link class="nav-link" to="/received">Received</router-link>
        <router-link class="nav-link" to="/albums">Albums</router-link>
        <router-link class="nav-link" to="/vendors">Vendors</router-link>
        <router-link class="nav-link" to="/my-contacts">My Contacts</router-link>
      </div>
      <div class="nav-actions d-none d-lg-flex">
        <div class="avatar-wrapper" ref="avatarRef">
          <button class="avatar-btn" @click.stop="toggleDropdown">
            <img :src="session.profile?.user_img || '/assets/img/av.png'" alt="User avatar" />
            <span class="d-none d-xl-inline">{{ session.profile?.name || 'User' }}</span>
            <i class="fas fa-chevron-down"></i>
          </button>
          <div v-if="dropdownOpen" class="user-dropdown" @click.stop>
            <div class="user-card">
              <img :src="session.profile?.user_img || '/assets/img/av.png'" alt="User avatar" />
              <div>
                <strong>{{ session.profile?.name || 'User' }}</strong>
                <p class="text-muted mb-0">{{ session.profile?.email }}</p>
              </div>
            </div>
            <router-link class="dropdown-item" to="/settings">
              <i class="fas fa-cog mr-2"></i> Settings
            </router-link>
            <button class="dropdown-item text-danger" @click="logout">
              <i class="fas fa-sign-out-alt mr-2"></i> Logout
            </button>
          </div>
        </div>
      </div>
      <button class="mobile-toggle d-lg-none" @click="toggleSidenav">
        <span></span><span></span><span></span>
      </button>
    </div>
  </nav>

  <!-- NOT LOGGED-IN NAVBAR -->
  <nav
    v-else
    class="app-navbar"
  >
    <div class="nav-container">
      <router-link class="brand" to="/">
        <img v-no-right-click src="/assets/logo.png" alt="Invi8" />
      </router-link>
      <div class="nav-actions d-none d-lg-flex">
        <router-link class="btn ghost" to="/login">Login</router-link>
        <router-link class="btn solid" to="/register">Register</router-link>
      </div>
      <button class="mobile-toggle d-lg-none" @click="toggleSidenav">
        <span></span><span></span><span></span>
      </button>
    </div>
  </nav>

  <!-- SIDENAV (mobile & tablet) -->
  <transition name="slide">
    <div v-if="showSidenav" class="sidenav-overlay" @click.self="closeSidenav">
      <aside class="sidenav">
        <div class="sidenav-header">
          <button class="close-btn" @click="closeSidenav" aria-label="Close menu">
            <span aria-hidden="true">&times;</span>
          </button>
          <div class="logo-row">
            <img v-no-right-click src="/assets/logo-icon.png" alt="Invi8" />
            <div>
              <h6 class="mb-0">Invi8</h6>
              <small class="text-muted">Your event companion</small>
            </div>
          </div>
        </div>

        <div v-if="session.isLoggedIn" class="user-info text-center mb-4">
          <img
            class="rounded-circle mb-2"
            :src="session.profile?.user_img || '/assets/img/av.png'"
            width="68"
            height="68"
          />
          <div class="font-weight-bold">{{ session.profile?.name || session.user?.name || 'User' }}</div>
          <small class="text-muted">{{ session.profile?.email || session.user?.email }}</small>
        </div>

        <nav class="sidenav-links">
          <p class="sidenav-label">Main</p>
          <router-link to="/host" @click="closeSidenav">
            <i class="fas fa-home"></i> Host
          </router-link>
          <router-link to="/received" @click="closeSidenav">
            <i class="fas fa-inbox"></i> Received
          </router-link>
          <router-link to="/albums" @click="closeSidenav">
            <i class="fas fa-images"></i> Albums
          </router-link>
          <router-link to="/vendors" @click="closeSidenav">
            <i class="fas fa-store"></i> Vendors
          </router-link>
          <router-link to="/my-contacts" @click="closeSidenav">
            <i class="fas fa-address-book"></i> My Contacts
          </router-link>

          <template v-if="session.isLoggedIn">
            <p class="sidenav-label mt-4">Account</p>
            <router-link to="/settings" @click="closeSidenav">
              <i class="fas fa-cog"></i> Settings
            </router-link>
            <button type="button" class="logout-btn" @click.prevent="logout">
              <i class="fas fa-sign-out-alt"></i> Logout
            </button>
          </template>

          <template v-else>
            <div class="auth-actions">
              <router-link to="/login" class="btn solid w-100" @click="closeSidenav">Login</router-link>
              <router-link to="/register" class="btn ghost w-100" @click="closeSidenav">Register</router-link>
            </div>
          </template>
        </nav>
      </aside>
    </div>
  </transition>
</template>

<script setup>
/* eslint-disable */
import { onMounted, watch, ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'

const router = useRouter()
const session = useSessionStore()

const showSidenav = ref(false)
const dropdownOpen = ref(false)
const avatarRef = ref(null)

const toggleSidenav = () => {
  showSidenav.value = !showSidenav.value
}

const closeSidenav = () => {
  showSidenav.value = false
}
 

const logout = () => {
  Swal.fire({
    title: 'Are you sure?',
    text: 'You will be logged out.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, logout',
    cancelButtonText: 'Cancel'
  }).then((result) => {
    if (result.isConfirmed) {
      session.logout()
      router.push('/login')
      Swal.fire('Logged Out', 'You have been logged out.', 'success')
      closeSidenav()
    }
  })
}

// Auto-close sidenav when route changes
router.afterEach(() => {
  closeSidenav()
  dropdownOpen.value = false
})

onMounted(() => {
  session.loadUser()
  session.fetchProfile() // fetch only once, cached in store
  document.addEventListener('click', handleClickOutside)
})

watch(
  () => session.userId,
  (id) => {
    if (id) {
      session.fetchProfile()
    }
  }
)

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
}


const handleClickOutside = (event) => {
  if (!avatarRef.value) return
  if (!avatarRef.value.contains(event.target)) {
    dropdownOpen.value = false
  }
}
 
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.app-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  z-index: 1030;
  box-shadow: 0 10px 30px rgba(15,23,42,0.08);
}

.nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 5vw;
}

.brand img {
  height: 40px;
}

.nav-links {
  gap: 8px;
}

.nav-link {
  font-weight: 600;
  color: #475569;
  padding: 10px 14px;
  border-radius: 10px;
  transition: background 0.15s ease, color 0.15s ease;
}
.nav-link.router-link-active,
.nav-link:hover {
  color: #0f172a;
  background: rgba(15,23,42,0.08);
}

.nav-actions {
  align-items: center;
  gap: 12px;
}

.btn.ghost {
  border: 1px solid rgba(15,23,42,0.15);
  border-radius: 999px;
  padding: 8px 18px;
  color: #0f172a;
  font-weight: 600;
}
.btn.solid {
  border: none;
  border-radius: 999px;
  padding: 8px 20px;
  background: #0f172a;
  color: white;
  font-weight: 600;
}

.mobile-toggle {
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mobile-toggle span {
  width: 22px;
  height: 2px;
  background: #0f172a;
  border-radius: 999px;
}

.avatar-wrapper {
  position: relative;
}

.avatar-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 999px;
  padding: 2px 12px 2px 2px;
  background: white;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
.avatar-btn img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}
.avatar-btn:hover {
  box-shadow: 0 8px 18px rgba(15,23,42,0.15);
}
.avatar-btn i {
  color: #94a3b8;
}

.user-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 12px);
  width: 260px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 45px rgba(15,23,42,0.12);
  padding: 16px;
  z-index: 1055;
}
.user-card {
  display: flex;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(15,23,42,0.06);
  margin-bottom: 12px;
}
.user-card img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}
.user-card div {
  min-width: 0;
}
.user-card strong {
  font-size: 0.95rem;
  display: block;
  color: #0f172a;
  line-height: 1.25;
}
.user-card p {
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dropdown-item {
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: 8px 0;
  color: #0f172a;
  font-weight: 500;
  display: flex;
  align-items: center;
}
.dropdown-item i {
  margin-right: 8px;
}
.dropdown-item.text-danger {
  color: #c53030;
}

/* --- SIDENAV STYLES --- */
.sidenav-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
  backdrop-filter: blur(3px);
}

.sidenav {
  width: 320px;
  max-width: 90%;
  height: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-top-left-radius: 24px;
  border-bottom-left-radius: 24px;
  padding: 24px 20px 32px;
  display: flex;
  flex-direction: column;
  box-shadow: -15px 0 35px rgba(15, 23, 42, 0.15);
  overflow-y: auto;
}

.sidenav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-row img {
  width: 44px;
  height: 44px;
}

.close-btn {
  background: rgba(15, 23, 42, 0.05);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: #0f172a;
}

.sidenav .user-info img {
  box-shadow: 0 10px 30px rgba(15,23,42,0.2);
}

.sidenav-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sidenav-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.sidenav-links a,
.sidenav-links button.logout-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.sidenav-links a:hover,
.sidenav-links button.logout-btn:hover {
  transform: translateX(3px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.sidenav-links i {
  width: 18px;
  text-align: center;
  color: #475569;
}

.logout-btn {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.25);
  color: #dc2626;
}

.auth-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.auth-actions .btn {
  border-radius: 14px;
  padding: 12px;
  text-align: center;
}

/* Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

@media (max-width: 991px) {
  .nav-links,
  .nav-actions {
    display: none !important;
  }
  .nav-container {
    padding: 10px 20px;
  }
}
</style>
